/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHeaderActions, xHeaderState } from "@tps/App/Store/Reducers";
import Close from "@tps/XDL/Icons/Close";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface GlobalWebsiteMessageStyles {
    container: InjectableCss;
    close: ButtonPresentationProps;
    textStyles: TypographyPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            text-align: center;
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    close: {
        css: css`
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            ${IconWrapper}, svg {
                width: 13px;
                height: 21px;
            }
        `,
    },
    textStyles: {
        css: css`
            &&&, p {
                text-align: center;
                font-size: 12px;
                line-height: 20px;
                color: ${getColor("common.white")};
                margin-bottom: 0;
                padding-right: 20px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    globalMessage = "globalMessage",
}

const mapStateToProps = (state: xHeaderState) => ({
    notificationBar: state.xHeader.notificationBar,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> & {
        fields: {
            [fields.globalMessage]: string;
        };
    };

const GlobalWebsiteMessage: React.FC<Props> = ({ fields: { globalMessage }, dispatch, notificationBar }) => {
    const [isClosed, setIsClosed] = React.useState(false);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const closeState = sessionStorage?.getItem('closed');
        if (closeState) {
            setIsClosed(true);
        } else {
            setIsClosed(false);
        }
    }, [notificationBar]);
    const onClose = () => {
        dispatch({ type: "xHeader/notificationBar", notificationBar: false });
        sessionStorage.setItem('closed', 'true');
    };

    return globalMessage?.length && !isClosed ? (
        <StyledWrapper {...globalWebsiteMessageStyles.container}>
            <Clickable
                {...globalWebsiteMessageStyles.close}
                onClick={onClose}
                data-test-selector="global_sitemessage_close"
                aria-label="close"
            >
                <Icon src={Close} size={13} color="common.white" />
            </Clickable>
            <Typography {...globalWebsiteMessageStyles.textStyles}>
                {globalMessage && parse(globalMessage, parserOptions)}
            </Typography>
        </StyledWrapper>
    ) : (
        <></>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Global Website Message",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.globalMessage,
            displayName: "Global Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(GlobalWebsiteMessage),
    definition,
};

export default widgetModule;
